import React from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import filterFactory from 'react-bootstrap-table2-filter';
import cellEditFactory from 'react-bootstrap-table2-editor';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css';
import 'react-bootstrap-table2-filter/dist/react-bootstrap-table2-filter.min.css';
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
  SizePerPageDropdownStandalone,
  PaginationTotalStandalone,
} from 'react-bootstrap-table2-paginator';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import { cloneDeep, isEqual } from 'lodash';
import ContextMenu from './ContextMenu';
import { Loading } from '../Common';
import { withTranslation } from 'react-i18next';
import {
  paginationTotalRenderer,
  sizePerPageRenderer,
  ExportDropdown,
  SearchBar,
  RemoteSearchBar,
  selectionHeaderRenderer,
} from './helpers';
import moment from 'moment';
import classNames from 'classnames';
import helper from '../../../utils/helper';
import './datagrid.scss';

function useDeepEffect(fn, deps) {
  const isFirst = React.useRef(true);
  const prevDeps = React.useRef(deps);

  React.useEffect(() => {
    const isFirstEffect = isFirst.current;
    const isSame = prevDeps.current.every((obj, index) =>
      isEqual(obj, deps[index]),
    );

    isFirst.current = false;
    prevDeps.current = deps;

    if (isFirstEffect || !isSame) {
      return fn();
    }
  }, deps);
}

function Datagrid({
  datagridProps,
  searchProps,
  exportProps = {},
  tableProps,
  noPagination,
  t,
  ...props
}) {
  const [sizePerPage, updateSizePerPage] = React.useState(25);
  const options = {
    custom: true,
    paginationSize: 4,
    pageStartIndex: 1,
    alwaysShowAllBtns: true, // Always show next and previous button
    withFirstAndLast: true, // Hide the going to First and Last page button
    firstPageText: t('First'),
    prePageText: t('Back'),
    nextPageText: t('Next'),
    lastPageText: t('Last'),
    paginationTotalRenderer: (from, to, size) =>
      paginationTotalRenderer(from, to, size, t),
    sizePerPageRenderer: (sizePerPageProps) =>
      sizePerPageRenderer(sizePerPageProps, updateSizePerPage),
    sizePerPage,
    ...getOptions(),
  };
  function getOptions() {
    if (props.remote) {
      return {
        sizePerPage: props.customPagination.sizePerPage,
        page: props.customPagination.page,
      };
    }
    if (noPagination) {
      return {
        sizePerPage: datagridProps.data.length,
      };
    }
  }
  const exportOptions = {
    ...exportProps,
    fileName: getExportFileName(),
  };
  function getExportFileName() {
    if (exportProps.customFileName) {
      return exportProps.fileName;
    }
    return `${exportProps.fileName}-${moment().format('MM-DD-YYYY')}.csv`;
  }
  const [dataSize, setDataSize] = React.useState(0);
  const [ctxState, setCtxState] = React.useState({
    openCtxMenu: false,
    ctxMenuCoord: null,
    ctxMenuRow: null,
    ctxMenuIdx: null,
    rowEvents: null,
  });
  React.useEffect(() => {
    setDataSize(datagridProps.data.length);
    let rowEvents = props.rowEvents || {};
    if (props.ctxMenuOptions) {
      let func = rowEvents.onContextMenu;
      rowEvents.onContextMenu = (e, row, rowIndex) => {
        if (props.onContext) props.onContext(row, rowIndex);
        if (func) func(e, row, rowIndex);
        e.preventDefault();
        let ctxMenuCoord = {};
        if (e) {
          ctxMenuCoord = Object.assign({}, { x: e.clientX, y: e.clientY });
        }
        setCtxState({
          ...ctxState,
          ctxMenuCoord,
          openCtxMenu: !ctxState.openCtxMenu,
          ctxMenuRow: row,
          ctxMenuIdx: rowIndex,
          rowEvents,
        });
      };
    }
    setCtxState({ ...ctxState, rowEvents });
  }, []);
  React.useEffect(() => {
    if (props.customPagination && props.customPagination.dataSize) {
      setDataSize(props.customPagination.dataSize);
    } else {
      setDataSize(datagridProps.data.length);
    }
  }, [
    datagridProps.data.length,
    props.customPagination && props.customPagination.dataSize,
  ]);
  function toggleContextMenu(e) {
    let update = { openCtxMenu: !ctxState.openCtxMenu };
    if (e) {
      update = Object.assign(update, {
        ctxMenuCoord: { x: e.clientX, y: e.clientY },
      });
    }
    setCtxState({ ...ctxState, ...update });
  }
  const { ctxMenuOptions } = props;
  const [columns, setColumns] = React.useState(datagridProps.columns);
  React.useEffect(() => {
    const newCols = datagridProps.columns.map((col) => {
      return {
        ...col,
        text: t(col.text),
        headerClasses: `fix-column-overflow${
          col.sort ? ' fix-column-sort' : ''
        }${col.isDateTime ? ' datetime-column' : ''}`,
        editable: col.editable ? true : false,
        sortFunc:
          col.isDateTime && col.sort
            ? helper.sortDateFunc
            : col.sortFunc
            ? col.sortFunc
            : null,
        align: col.isDateTime ? 'center' : 'left',
        // style: { textAlign: col.isDateTime && 'center', ...col.style },
      };
    });
    setColumns(newCols);
  }, [datagridProps.columns, props.i18n.language]);
  function afterSearch(result) {
    setDataSize(result.length);
  }

  return (
    <div className={props.containerClassName} style={props.containerStyle}>
      <PaginationProvider
        pagination={paginationFactory({
          ...options,
          totalSize: dataSize,
        })}
      >
        {({ paginationProps, paginationTableProps }) => {
          return (
            <>
              <ToolkitProvider
                {...datagridProps}
                columns={columns}
                search={{ afterSearch, searchFormatted: false }}
                exportCSV={exportOptions}
              >
                {(toolkitProps) => {
                  return (
                    <>
                      {ctxMenuOptions ? (
                        <ContextMenu
                          {...ctxState}
                          id={props.ctxMenuId}
                          ctxMenuOptions={ctxMenuOptions}
                          toggleFunc={toggleContextMenu}
                        />
                      ) : null}
                      {!props.noSearch && (
                        <>
                          {searchProps && searchProps.remote ? (
                            <RemoteSearchBar
                              placeholder={t('Search')}
                              {...searchProps}
                              onPageChange={paginationProps.onPageChange}
                            />
                          ) : (
                            <SearchBar
                              placeholder={t('Search')}
                              {...searchProps}
                              {...toolkitProps.searchProps}
                              onSearch={
                                searchProps && searchProps.custom
                                  ? searchProps.onSearch
                                  : toolkitProps.searchProps.onSearch
                              }
                              onPageChange={paginationProps.onPageChange}
                            />
                          )}
                          <hr />
                        </>
                      )}
                      <BootstrapTable
                        {...toolkitProps.baseProps}
                        {...paginationTableProps}
                        {...datagridProps}
                        remote={props.remote}
                        columns={columns}
                        hover
                        condensed
                        defaultSortDirection="asc"
                        rowEvents={ctxState.rowEvents}
                        bootstrap4
                        wrapperClasses={classNames(props.wrapperClasses, {
                          'table-wrapper-scrollX': props.scrollX,
                        })}
                        classes={classNames(props.classes, {
                          'table-scrollX table-responsive': props.scrollX,
                        })}
                        headerClasses="table-header"
                        noDataIndication={
                          datagridProps.loading ? <Loading /> : t('No result')
                        }
                        // selectRow={{
                        //   ...datagridProps.selectRow,
                        //   selectionHeaderRenderer: selectionHeaderRenderer,
                        // }}
                        filter={filterFactory()}
                        cellEdit={cellEditFactory({
                          mode: 'dbclick',
                          blurToSave: true,
                          afterSaveCell: props.onEditCell,
                        })}
                        onTableChange={props.onTableChange}
                      />
                      <div className="d-flex justify-content-between">
                        <div className="d-flex align-items-center">
                          {!noPagination && (
                            <>
                              <SizePerPageDropdownStandalone
                                {...paginationProps}
                              />
                              <PaginationTotalStandalone {...paginationProps} />
                            </>
                          )}
                        </div>
                        <div className="d-flex align-items-center">
                          {!noPagination && (
                            <PaginationListStandalone {...paginationProps} />
                          )}
                          {!props.noExport && (
                            <ExportDropdown
                              {...toolkitProps.csvProps}
                              customExportProps={props.customExportProps}
                            />
                          )}
                        </div>
                      </div>
                    </>
                  );
                }}
              </ToolkitProvider>
            </>
          );
        }}
      </PaginationProvider>
    </div>
  );
}

Datagrid.defaultProps = {
  noPagination: false,
  noSearch: false,
  noExport: false,
};

export default withTranslation('app')(Datagrid);
